import { Theme, createTheme } from "@mui/material/styles";
import { SxProps } from "@mui/system/styleFunctionSx";

import type {} from "@mui/x-date-pickers/themeAugmentation";

// Extend Avatar to support custom variants and sizes
declare module "@mui/material/Avatar" {
  interface AvatarPropsVariantOverrides {
    outlined: true;
  }
  interface AvatarOwnProps {
    size?: "small" | "medium" | "large";
  }
}

// Extend Paper to support custom summary and alert variants
declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    summary: true;
    alertError: true;
    alertSuccess: true;
    alertWarning: true;
  }
}

// Extend Button to support custom card, dialog, filter, capsule, and outlinedRect variants
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    card: true;
    outlined: true;
    dialog: true;
    filter: true;
    capsule: true;
    outlinedRect: true;
  }
}

// Extend theme to support custom text.hint color and lab typography variants
declare module "@mui/material/styles" {
  interface TypeText {
    hint: string;
  }
  interface TypographyVariants {
    labValue: React.CSSProperties;
    labUnit: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    labValue?: React.CSSProperties;
    labUnit?: React.CSSProperties;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    labValue: true;
    labUnit: true;
  }
}

// Extend Typography to support custom vitalValue variant
declare module "@mui/material/styles" {
  interface TypographyVariants {
    vitalValue: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    vitalValue?: React.CSSProperties;
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    vitalValue: true;
  }
}

// Extend palette and mixins to support custom colors and DataGrid
declare module "@mui/material/styles" {
  interface Palette {
    orange: Palette["primary"];
    alertRed: Palette["primary"];
    alertOrange: Palette["primary"];
    alertYellow: Palette["primary"];
    alertGreen: Palette["primary"];
    alertTeal: Palette["primary"];
    purple: Palette["primary"];
    chart: { systolic: string; diastolic: string };
  }
  interface PaletteOptions {
    orange?: PaletteOptions["primary"];
    alertRed?: PaletteOptions["primary"];
    alertOrange?: PaletteOptions["primary"];
    alertYellow?: PaletteOptions["primary"];
    alertGreen?: PaletteOptions["primary"];
    alertTeal?: PaletteOptions["primary"];
    purple?: PaletteOptions["primary"];
    chart?: { systolic: string; diastolic: string };
  }
  interface PaletteColor {
    tableRow?: string;
    badgeActive?: string;
    badgeInactive?: string;
  }
  interface SimplePaletteColorOptions {
    tableRow?: string;
    badgeActive?: string;
    badgeInactive?: string;
  }
  interface Mixins {
    MuiDataGrid?: {
      containerBackground?: string;
    };
  }
}

// NOTE: Here's a good resource for MUI sx styles: https://muhimasri.com/blogs/mui-textfield-colors-styles/

// TODO: Dark Mode Readiness
// 1. Fix text.secondary - currently white (rgba(255,255,255,1)) which is wrong for light mode
//    Should be a dark gray like #757575
// 2. Add missing action properties: hover, selected, disabled, disabledBackground, focus
// 3. Create a separate dark theme or use MUI's mode-based palette
// 4. Update ColorModeContext to use different theme for dark mode
//    (currently: theme={mode === "light" ? appTheme : appTheme} - both use same theme)
// 5. Make ToastContainer in App.tsx theme-aware (currently hardcoded to "dark")

export const appTheme = createTheme({
  mixins: {
    MuiDataGrid: {
      // Required by MUI X DataGrid v7+ for header styling. Value must match palette.primary.main
      containerBackground: "#2577A0",
    },
  },
  palette: {
    primary: {
      main: "#2577A0",
      light: "#5487AC",
      dark: "#1E668A",
      contrastText: "#E7F3F9",
    },
    secondary: {
      main: "#EF5455",
      light: "#F27677",
      // dark: 'string',
      // contrastText: 'string',
    },
    action: {
      active: "#F1FAFB",
      hover: "rgba(26, 26, 26, 0.04)",
      selected: "rgba(26, 26, 26, 0.08)",
      disabled: "#6C6C6C",
      disabledBackground: "#F5F6F9",
      focus: "rgba(26, 26, 26, 0.12)",
      shadow: "rgba(26, 26, 26, 0.24)",
      shadowDark: "rgba(0, 0, 0, 0.25)",
    },
    background: {
      default: "#F5F6F9",
      paper: "#ffffff",
      input: "#F5F6F9",
      page: "#F1FAFB",
      blueGrey: "#EDF5FB",
    },
    text: {
      primary: "#1A1A1A",
      secondary: "#757575",
      disabled: "#6C6C6C",
      hint: "#646464",
    },
    success: {
      main: "#4CAF50",
      light: "#81C784",
      dark: "#388E3C",
      contrastText: "#ffffff",
    },
    error: {
      main: "#DC143C1A",
      light: "#FFE5E5",
      dark: "#DC143C",
    },
    warning: {
      main: "#FFD7001A",
      light: "#FFFADDFC",
      dark: "#FFD700",
      contrastText: "#926843",
    },
    info: {
      light: "#FCF8F8",
      main: "#19B5CD",
      dark: "#BBCEF4",
      tableRow: "rgba(237, 245, 251, 0.61)",
      badgeActive: "rgba(132, 163, 192, 0.72)",
      badgeInactive: "rgba(132, 163, 192, 0.15)",
    },
    orange: {
      main: "#FD7608",
      light: "#FFF1E6",
    },
    // Alert indicator colors from Figma (main = 10% opacity bg, dark = solid border)
    alertRed: {
      main: "#DC35451A",
      dark: "#DC3545",
    },
    alertOrange: {
      main: "#FD76081A",
      dark: "#FD7608",
    },
    alertYellow: {
      main: "#FFC1071A",
      dark: "#FFC107",
    },
    alertGreen: {
      main: "#E8F5E9",
      dark: "#4CAF50",
      light: "#7EB77F",
    },
    alertTeal: {
      main: "#19B5CD1A",
      dark: "#19B5CD",
    },
    purple: {
      main: "#F9F2F8",
      dark: "#601856",
    },
    chart: {
      systolic: "#2577A0",
      diastolic: "#721D6C",
    },
    divider: "#E8E9F0",
    grey: {
      50: "#F5F5F5",
      100: "#C4C7C7",
      200: "#F1EDEC",
      300: "#D9D9D9",
      400: "#BDBDBD",
      500: "#949494",
      700: "#747878",
      900: "#1A1A1A",
    },
  },
  typography: {
    fontFamily: "Mulish, sans-serif",
    h1: {
      fontSize: "32px",
      fontWeight: 500,
      textAlign: "center",
    },
    h2: {
      fontSize: "16px",
      fontWeight: 400,
    },
    h3: {
      fontSize: "24px",
      fontWeight: 700,
    },
    h4: {
      fontSize: "16px",
      fontWeight: 700,
    },
    h5: {
      fontSize: "20px",
      fontWeight: 400,
    },
    h6: {
      fontSize: "16px",
      fontWeight: 500,
    },
    vitalValue: {
      fontSize: "22px",
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: "16px",
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: "14px",
      fontWeight: 600,
    },
    body1: {
      fontSize: "14px",
      fontWeight: 500,
    },
    body2: {
      fontSize: "14px",
      fontWeight: 400,
    },
    caption: {
      fontSize: "12px",
      fontWeight: 400,
    },
    overline: {
      fontSize: "13px",
      fontWeight: 600,
      letterSpacing: "0.5px",
    },
    labValue: {
      fontSize: "32px",
      fontWeight: 300,
    },
    labUnit: {
      fontSize: "18px",
      fontWeight: 600,
    },
    button: {
      textTransform: "none",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        // contained: {
        // 	height: '28px',
        // 	padding: '4px 8px',
        // 	borderRadius: '4px',
        // 	// boxShadow: 'none',
        // 	// '&:hover': {
        // 	// 	boxShadow: 'none',
        // 	// },
        // },
      },
      variants: [
        // Primary color variant (default for CardButton)
        {
          props: { variant: "card", color: "primary" },
          style: ({ theme }) => ({
            height: 52,
            borderRadius: 50, // Figma spec: fully rounded pill
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`, // Figma Shadow 1
            textTransform: "none",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`, // Elevated on hover
              backgroundColor: theme.palette.primary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: "none",
            },
          }),
        },
        // Fallback for card variant without color prop
        {
          props: { variant: "card" },
          style: ({ theme }) => ({
            height: 52,
            borderRadius: 50, // Figma spec: fully rounded pill
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`, // Figma Shadow 1
            textTransform: "none",
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`, // Elevated on hover
              backgroundColor: theme.palette.primary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: "none",
            },
          }),
        },
        // Secondary color variant (Deactivate button)
        {
          props: { variant: "card", color: "secondary" },
          style: ({ theme }) => ({
            height: 52,
            borderRadius: 50,
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            textTransform: "none",
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`,
              backgroundColor: theme.palette.secondary.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: "none",
            },
          }),
        },
        // Success color variant (Reactivate button)
        {
          props: { variant: "card", color: "success" },
          style: ({ theme }) => ({
            height: 52,
            borderRadius: 50,
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            textTransform: "none",
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`,
              backgroundColor: theme.palette.success.dark,
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              boxShadow: "none",
            },
          }),
        },
        // Outlined variant (for back button in add patient modal)
        {
          props: { variant: "outlined", color: "primary" },
          style: ({ theme }) => ({
            height: 52,
            borderRadius: 50,
            textTransform: "none",

            // "outlined" look
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
            border: `2px solid ${theme.palette.primary.main}`,
            boxShadow: "none",

            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.primary.dark,
              color: theme.palette.primary.dark,
              boxShadow: "none",
            },

            "&.Mui-disabled": {
              backgroundColor: theme.palette.common.white,
              color: theme.palette.action.disabled,
              borderColor: theme.palette.action.disabledBackground,
              boxShadow: "none",
            },
          }),
        },

        // Fallback for cardOutlined variant without color prop
        {
          props: { variant: "outlined" },
          style: ({ theme }) => ({
            height: 52,
            borderRadius: 50,
            textTransform: "none",

            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
            border: `2px solid ${theme.palette.primary.main}`,
            boxShadow: "none",

            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.primary.dark,
              color: theme.palette.primary.dark,
              boxShadow: "none",
            },

            "&.Mui-disabled": {
              backgroundColor: theme.palette.common.white,
              color: theme.palette.action.disabled,
              borderColor: theme.palette.action.disabledBackground,
              boxShadow: "none",
            },
          }),
        },
        // Dialog cancel button (white bg, primary text)
        {
          props: { variant: "dialog" },
          style: ({ theme }) => ({
            height: 32,
            borderRadius: 29,
            padding: "0 16px",
            textTransform: "none",
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.primary.main}`,
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`,
              backgroundColor: theme.palette.common.white,
            },
            // Applies to every dialog button (incl. primary/error/success) since the
            // .Mui-disabled selector out-specifies the per-color root styles.
            "&.Mui-disabled": {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled,
              border: "none",
              boxShadow: "none",
            },
          }),
        },
        // Dialog destructive button (error bg, white text)
        {
          props: { variant: "dialog", color: "error" },
          style: ({ theme }) => ({
            height: 32,
            borderRadius: 29,
            padding: "0 16px",
            textTransform: "none",
            border: "none",
            color: theme.palette.common.white,
            backgroundColor: theme.palette.error.dark,
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`,
              backgroundColor: theme.palette.error.dark,
              opacity: 0.9,
            },
          }),
        },
        // Dialog save/primary button (primary bg, white text)
        {
          props: { variant: "dialog", color: "primary" },
          style: ({ theme }) => ({
            height: 32,
            borderRadius: 29,
            padding: "0 16px",
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`,
              backgroundColor: theme.palette.primary.dark,
            },
          }),
        },
        // Dialog success button (success bg, white text)
        {
          props: { variant: "dialog", color: "success" },
          style: ({ theme }) => ({
            height: 32,
            borderRadius: 29,
            padding: "0 16px",
            textTransform: "none",
            border: "none",
            color: theme.palette.common.white,
            backgroundColor: theme.palette.success.main,
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            "&:hover": {
              boxShadow: `0 4px 6px 0 ${theme.palette.action.shadow}`,
              backgroundColor: theme.palette.success.dark,
            },
          }),
        },
        // Rectangular outlined button (white bg, blue text, blue border — no pill shape)
        // Padding reduced by 1px top/bottom to offset the 1px border, keeping height identical to contained
        {
          props: { variant: "outlinedRect" },
          style: ({ theme }) => ({
            textTransform: "none",
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: theme.shape.borderRadius,
            boxShadow: "none",
            padding: "5px 15px", // medium: 6px 16px (contained default) minus 1px border each side
            "&.MuiButton-sizeSmall": {
              padding: "3px 9px", // small: 4px 10px minus 1px
            },
            "&.MuiButton-sizeLarge": {
              padding: "7px 21px", // large: 8px 22px minus 1px
            },
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.primary.dark,
              color: theme.palette.primary.dark,
              boxShadow: "none",
            },
            "&.Mui-disabled": {
              backgroundColor: theme.palette.common.white,
              color: theme.palette.action.disabled,
              borderColor: theme.palette.action.disabledBackground,
            },
          }),
        },
        // Small card variant — half-height pill (36px) for compact layouts
        {
          props: { variant: "card", size: "small" },
          style: { height: 36, borderRadius: 50 },
        },
        // Small outlined variant — half-height pill (36px) for compact layouts
        {
          props: { variant: "outlined", size: "small" },
          style: { height: 36, borderRadius: 50 },
        },
        // Filter button (pill-shaped, grey bg, shadow — matches card variant shape)
        {
          props: { variant: "filter" },
          style: ({ theme }) => ({
            ...theme.typography.overline,
            height: 32,
            borderRadius: 50,
            backgroundColor: theme.palette.divider,
            color: theme.palette.text.hint,
            boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            border: "none",
            "&:hover": {
              backgroundColor: theme.palette.grey[300],
              boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
            },
          }),
        },
        // Capsule button error (red bg, white text, large padding)
        {
          props: { variant: "capsule", color: "error" },
          style: ({ theme }) => ({
            borderRadius: theme.spacing(6),
            padding: theme.spacing(1, 6),
            textTransform: "none",
            backgroundColor: theme.palette.error.dark,
            color: theme.palette.common.white,
            "&:hover": {
              backgroundColor: theme.palette.error.main,
            },
          }),
        },
        // Capsule button success (green bg, white text, large padding)
        {
          props: { variant: "capsule", color: "success" },
          style: ({ theme }) => ({
            borderRadius: theme.spacing(6),
            padding: theme.spacing(1, 6),
            textTransform: "none",
            backgroundColor: theme.palette.success.main,
            color: theme.palette.common.white,
            "&:hover": {
              backgroundColor: theme.palette.success.dark,
            },
          }),
        },
        // Outlined capsule button (primary border, large padding)
        {
          props: { variant: "outlined", size: "large" },
          style: ({ theme }) => ({
            borderRadius: theme.spacing(6),
            padding: theme.spacing(1, 4),
          }),
        },
        // Warning outlined button (warning border and text)
        {
          props: { variant: "outlined", color: "warning" },
          style: ({ theme }) => ({
            borderRadius: theme.spacing(6),
            padding: theme.spacing(1, 4),
            borderColor: theme.palette.warning.dark,
            color: theme.palette.warning.dark,
            "&:hover": {
              borderColor: theme.palette.warning.dark,
              backgroundColor: theme.palette.warning.main,
            },
          }),
        },
      ],
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(4),
          "&:last-child": {
            paddingBottom: theme.spacing(4),
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          position: "relative",
          borderRadius: 16,
          boxShadow: `0 4px 11px 0 ${theme.palette.action.shadow}`,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          justifyContent: "center",
          padding: theme.spacing(0, 3, 3),
        }),
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 48,
          height: 48,
        },
      },
      variants: [
        // Size variants
        {
          props: { size: "small" },
          style: {
            width: 40,
            height: 40,
          },
        },
        {
          props: { size: "large" },
          style: {
            width: 60,
            height: 60,
          },
        },
        // Outlined variant
        {
          props: { variant: "outlined" },
          style: ({ theme }) => ({
            width: 42,
            height: 42,
            backgroundColor: "transparent",
            border: `1px solid ${theme.palette.grey[500]}`,
            color: theme.palette.grey[500],
          }),
        },
        {
          props: { variant: "outlined", color: "primary" as const },
          style: ({ theme }) => ({
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          }),
        },
      ],
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: theme.spacing(2),
          borderBottom: `1px solid ${theme.palette.divider}`,
          "&:last-child": {
            borderBottom: 0,
          },
          "&:hover": {
            backgroundColor: theme.palette.action.active,
          },
          "&.Mui-selected": {
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.action.active,
            "&:hover": {
              backgroundColor: theme.palette.action.active,
            },
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderRadius: 8,
        },
      },
      variants: [
        {
          props: { variant: "summary" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.grey[50],
            borderRadius: 16,
            boxShadow: theme.shadows[1],
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
        {
          props: { variant: "alertError" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.error.light,
            border: `1px solid ${theme.palette.error.main}`,
            borderRadius: theme.spacing(2),
            padding: theme.spacing(3),
            marginBottom: theme.spacing(3),
          }),
        },
        {
          props: { variant: "alertSuccess" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.alertGreen.main,
            border: `1px solid ${theme.palette.alertGreen.dark}`,
            borderRadius: theme.spacing(2),
            padding: theme.spacing(3),
            marginBottom: theme.spacing(3),
          }),
        },
        {
          props: { variant: "alertWarning" },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.warning.light,
            border: `1px solid ${theme.palette.alertYellow?.dark ?? theme.palette.warning.dark}`,
            borderRadius: theme.spacing(2),
            padding: theme.spacing(3),
            marginBottom: theme.spacing(3),
          }),
        },
      ],
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography.overline,
          border: "none",
          borderRadius: "20px !important",
          color: theme.palette.text.hint,
          backgroundColor: "transparent",
          "&.Mui-selected": {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          },
        }),
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.grey[300],
          borderRadius: "20px",
          overflow: "hidden",
          height: 37,
        }),
        grouped: {
          border: "none",
          "&:not(:first-of-type)": {
            borderLeft: "none",
            marginLeft: 0,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          "&.Mui-selected": {
            color: theme.palette.text.primary,
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        button: {
          border: "none",
          background: "none",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          "&.MuiInputLabel-shrink": {
            color: theme.palette.primary.main,
            fontSize: theme.typography.caption.fontSize,
            transform: "translate(0, -20px) scale(1)",
            "&.Mui-focused": {
              color: theme.palette.primary.main,
            },
          },
          "&.Mui-error": {
            color: theme.palette.error.dark,
          },
          // Style the asterisk for required fields
          "& .MuiInputLabel-asterisk": {
            color: theme.palette.secondary.main, // #EF5455
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.input,
          borderRadius: theme.spacing(0.875), // 7px
          height: theme.spacing(5.125), // 41px
          overflow: "hidden",
          boxShadow: `0 2px 3px 0 ${theme.palette.action.shadow}`,
          "& fieldset": {
            borderColor: theme.palette.divider,
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            maxWidth: 0,
          },
          "&:hover fieldset": {
            borderColor: theme.palette.divider,
          },
          "&.Mui-focused fieldset": {
            borderColor: theme.palette.primary.main,
          },
          "&.Mui-error fieldset": {
            borderColor: theme.palette.error.dark,
          },
          "& input": {
            height: "100%",
            boxSizing: "border-box",
            padding: `0 ${theme.spacing(1.75)}`, // 14px
          },
          "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.input} inset`,
            WebkitTextFillColor: "inherit",
            caretColor: "inherit",
            height: theme.spacing(5.125), // 41px
            boxSizing: "border-box",
          },
          // Select dropdowns: match input field background
          "&:has(.MuiSelect-select)": {
            backgroundColor: theme.palette.background.input,
            borderRadius: 4,
            boxShadow: "0px 0px 0px 1px rgba(0,0,0,0.05), 0px 1px 2px 0px rgba(0,0,0,0.12)",
          },
        }),
      },
    },
    MuiPickersArrowSwitcher: {
      styleOverrides: {
        button: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        switchViewButton: ({ theme }) => ({
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          "&.Mui-error": {
            color: theme.palette.error.dark,
          },
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: "1.5px",
          backgroundColor: theme.palette.grey[100],
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        tag: ({ theme }) => ({
          "& .MuiChip-label": {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          },
        }),
        root: ({ theme }) => ({
          "& .MuiAutocomplete-popupIndicator": {
            color: theme.palette.text.primary,
          },
        }),
        inputRoot: ({ theme }) => ({
          height: "auto",
          minHeight: theme.spacing(5.125), // 41px - matches MuiOutlinedInput
          backgroundColor: theme.palette.background.input,
          borderRadius: theme.spacing(0.875), // 7px
          padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`, // 4px 12px
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: 41,
          backgroundColor: theme.palette.background.input,
          borderRadius: "7px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.divider,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.primary.main,
          },
          "& .MuiSelect-icon": {
            color: theme.palette.grey[500],
          },
          // Filter variant: compact dark dropdown for alert filter controls
          "&.filter-select": {
            height: theme.spacing(3.5),
            minWidth: theme.spacing(17.5),
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.common.white,
            borderRadius: theme.shape.borderRadius,
            boxShadow: "none",
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiSelect-select": {
              paddingTop: 0,
              paddingBottom: 0,
            },
            "& .MuiSelect-icon": {
              color: theme.palette.common.white,
            },
          },
        }),
        icon: {
          color: "#193154",
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.text.primary,
          ...theme.typography.caption,
          fontWeight: theme.typography.fontWeightBold,
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          margin: 0,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        sizeSmall: ({ theme }) => ({
          padding: 0,
          marginRight: theme.spacing(1),
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        sizeSmall: ({ theme }) => ({
          padding: 0,
          marginRight: theme.spacing(1),
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          // Default color (no color prop) → text.primary instead of action.active (#F1FAFB)
          ...(ownerState.color === "default" && {
            color: theme.palette.text.primary,
          }),
          "&.editButton": {
            color: theme.palette.grey[700],
            "&:hover": {
              color: theme.palette.grey[700],
              backgroundColor: "transparent",
            },
          },
        }),
      },
    },
  },
});

export const getDisabledButtonStyle = (theme: Theme): SxProps<Theme> => ({
  backgroundColor: theme.palette.action.disabledBackground,
  color: theme.palette.action.disabled,
  "&:hover": {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  opacity: 0.7,
});
